import { createAsyncThunk, createSlice, current } from '@reduxjs/toolkit'
import axios from 'axios'

const initialState = {
	entities: {},
	ids: [],
	isLoading: false,
	isInitialized: false,
	tasks: {
		entities: {},
		ids: [],
		isLoading: false,
		isInitialized: false,
		error: null,
	},
	siteDetails: {
		entities: {},
		ids: [],
		isLoading: false,
		isInitialized: false,
		error: null,
	},
	scoringCriteria: {
		entities: {},
		ids: [],
		isLoading: false,
		isInitialized: false,
		error: null,
	},
	financialLineItems: {
		entities: {},
		ids: [],
		isLoading: false,
		isInitialized: false,
		error: null,
	},
	risks: {
		entities: {},
		ids: [],
		isLoading: false,
		isInitialized: false,
		error: null,
	},
	prosCons: {
		entities: {},
		ids: [],
		isLoading: false,
		isInitialized: false,
		error: null,
	},
}

function prepConfig(getState) {
	const {
		userLogin: { userInfo },
	} = getState()

	return {
		headers: {
			Authorization: `Bearer ${userInfo.token}`,
		},
	}
}

export const createTemplate = createAsyncThunk(
	'templates/create',
	async (template: any, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.post(`/api/templates/`, template, prepConfig(getState))
			return data
		} catch (error) {
			return rejectWithValue('Failed to create template')
		}
	},
)

export const createTemplateSiteDetail = createAsyncThunk(
	'templates/createSiteDetail',
	async (sitedetail: any, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.post(
				`/api/templates/sitedetail`,
				sitedetail,
				prepConfig(getState),
			)
			return data
		} catch (error) {
			return rejectWithValue('Failed to create template')
		}
	},
)

export const createTemplateTask = createAsyncThunk(
	'templates/createTask',
	async (task: any, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.post(`/api/templates/task`, task, prepConfig(getState))
			return data
		} catch (error) {
			return rejectWithValue('Failed to create template task')
		}
	},
)

export const initializeTemplates = createAsyncThunk(
	'templates/initialize',
	async (_payload: void, { getState, rejectWithValue }) => {
		try {
			return axios.get('/api/templates', prepConfig(getState)).then(res => res.data)
		} catch (error) {
			return rejectWithValue('Failed to initialize templates')
		}
	},
)

export const initializeAdminTemplates = createAsyncThunk(
	'templates/initializeAdmin',
	async (_, { getState, rejectWithValue }) => {
		try {
			return axios.get('/api/templates/admin', prepConfig(getState)).then(res => res.data)
		} catch (error) {
			return rejectWithValue('Failed to initialize templates')
		}
	},
)

export const initializeTemplateTasks = createAsyncThunk(
	'templates/initializeTasks',
	async (templateId: any, { getState, rejectWithValue }) => {
		try {
			return axios
				.get(`/api/templates/${templateId}/tasks`, prepConfig(getState))
				.then(res => res.data)
		} catch (error) {
			return rejectWithValue('Failed to initialize template tasks')
		}
	},
)

export const initializeTemplateSiteDetails = createAsyncThunk(
	'templates/initializeSiteDetails',
	async (templateId: any, { getState, rejectWithValue }) => {
		try {
			return axios
				.get(`/api/templates/${templateId}/sitedetails`, prepConfig(getState))
				.then(res => res.data)
		} catch (error) {
			return rejectWithValue('Failed to initialize template site details')
		}
	},
)

export const updateTemplate = createAsyncThunk(
	'templates/update',
	async (template: any, { getState, rejectWithValue }) => {
		if (template && template._id) {
			try {
				const { data } = await axios.put(
					`/api/templates/${template._id}`,
					template,
					prepConfig(getState),
				)
				return data
			} catch (error) {
				console.error(error)
				return rejectWithValue('Unable to update template')
			}
		}
		return rejectWithValue('Malformed template')
	},
)

export const updateTemplateTask = createAsyncThunk(
	'templates/updateTask',
	async (task: any, { getState, rejectWithValue }) => {
		if (task && task._id) {
			try {
				const { data } = await axios.put(
					`/api/templates/${task._id}/task`,
					task,
					prepConfig(getState),
				)
				return data
			} catch (error) {
				console.error(error)
				return rejectWithValue('Unable to update task')
			}
		}
		return rejectWithValue('Malformed template task')
	},
)

export const updateTemplateSiteDetail = createAsyncThunk(
	'templates/updateSiteDetail',
	async (sitedetail: any, { getState, rejectWithValue }) => {
		if (sitedetail && sitedetail._id) {
			try {
				const { data } = await axios.put(
					`/api/templates/${sitedetail._id}/sitedetail`,
					sitedetail,
					prepConfig(getState),
				)
				return data
			} catch (error) {
				console.error(error)
				return rejectWithValue('Unable to update template')
			}
		}
		return rejectWithValue('Malformed template')
	},
)

export const deleteTemplate = createAsyncThunk(
	'templates/delete',
	async (templateId: any, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.delete(`/api/templates/${templateId}`, prepConfig(getState))
			return data
		} catch (error) {
			return rejectWithValue('Failed to delete template')
		}
	},
)

export const deleteTemplateSiteDetail = createAsyncThunk(
	'templates/deleteSiteDetail',
	async (sitedetailId: any, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.delete(
				`/api/templates/${sitedetailId}/sitedetail`,
				prepConfig(getState),
			)
			return data
		} catch (error) {
			return rejectWithValue('Failed to delete site detail')
		}
	},
)

export const deleteTemplateTask = createAsyncThunk(
	'templates/deleteTask',
	async (taskId: any, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.delete(`/api/templates/${taskId}/task`, prepConfig(getState))
			return data
		} catch (error) {
			return rejectWithValue('Failed to delete site detail')
		}
	},
)

export const buildTemplateFromTasks = createAsyncThunk(
	'templates/buildTemplateFromTasks',
	async (templateData: any, { getState, rejectWithValue }) => {
		try {
			const { templateName, description, accessLevel, selectedTasks, selectedTaskIds } = templateData
			const { data } = await axios.post(
				`/api/templates/build`,
				{
					templateName,
					description,
					accessLevel,
					selectedTaskIds,
					selectedTasks,
				},
				prepConfig(getState),
			)
			return data
		} catch (error: any) {
			return rejectWithValue(error.response?.data?.message || 'Failed to build template')
		}
	},
)

export const createTemplateScoringCriterion = createAsyncThunk(
	'templates/createScoringCriterion',
	async (criterion: any, { getState, rejectWithValue }) => {
		try {
			const { templateId, ...body } = criterion
			const { data } = await axios.post(
				`/api/templates/${templateId}/scoringCriterion`,
				body,
				prepConfig(getState),
			)
			return data
		} catch (error) {
			return rejectWithValue('Failed to create scoring criterion')
		}
	},
)

export const initializeTemplateScoringCriteria = createAsyncThunk(
	'templates/initializeScoringCriteria',
	async (templateId: any, { getState, rejectWithValue }) => {
		try {
			return axios
				.get(`/api/templates/${templateId}/scoringCriterion`, prepConfig(getState))
				.then(res => res.data)
		} catch (error) {
			return rejectWithValue('Failed to initialize scoring criteria')
		}
	},
)

export const updateTemplateScoringCriterion = createAsyncThunk(
	'templates/updateScoringCriterion',
	async (payload: any, { getState, rejectWithValue }) => {
		if (payload && payload._id && payload.templateId) {
			try {
				const { templateId, _id, ...body } = payload
				const { data } = await axios.put(
					`/api/templates/${templateId}/scoringCriterion/${_id}`,
					body,
					prepConfig(getState),
				)
				return data
			} catch (error) {
				return rejectWithValue('Unable to update scoring criterion')
			}
		}
		return rejectWithValue('Malformed scoring criterion (templateId and _id required)')
	},
)

export const deleteTemplateScoringCriterion = createAsyncThunk(
	'templates/deleteScoringCriterion',
	async (payload: any, { getState, rejectWithValue }) => {
		if (payload && payload._id && payload.templateId) {
			try {
				const { data } = await axios.delete(
					`/api/templates/${payload.templateId}/scoringCriterion/${payload._id}`,
					prepConfig(getState),
				)
				// Return the deleted id so the reducer can drop it from local state
				// (deleteTemplateTask leans on action.meta.arg, which only works when
				// the arg IS the id; here the arg is an object, so we return the id).
				return { _id: payload._id }
			} catch (error) {
				return rejectWithValue('Failed to delete scoring criterion')
			}
		}
		return rejectWithValue('Malformed scoring criterion (templateId and _id required)')
	},
)

export const createTemplateFinancialLineItem = createAsyncThunk(
	'templates/createFinancialLineItem',
	async (lineItem: any, { getState, rejectWithValue }) => {
		try {
			const { templateId, ...body } = lineItem
			const { data } = await axios.post(
				`/api/templates/${templateId}/financialLineItem`,
				body,
				prepConfig(getState),
			)
			return data
		} catch (error) {
			return rejectWithValue('Failed to create financial line item')
		}
	},
)

export const initializeTemplateFinancialLineItems = createAsyncThunk(
	'templates/initializeFinancialLineItems',
	async (templateId: any, { getState, rejectWithValue }) => {
		try {
			return axios
				.get(`/api/templates/${templateId}/financialLineItem`, prepConfig(getState))
				.then(res => res.data)
		} catch (error) {
			return rejectWithValue('Failed to initialize financial line items')
		}
	},
)

export const updateTemplateFinancialLineItem = createAsyncThunk(
	'templates/updateFinancialLineItem',
	async (payload: any, { getState, rejectWithValue }) => {
		if (payload && payload._id && payload.templateId) {
			try {
				const { templateId, _id, ...body } = payload
				const { data } = await axios.put(
					`/api/templates/${templateId}/financialLineItem/${_id}`,
					body,
					prepConfig(getState),
				)
				return data
			} catch (error) {
				return rejectWithValue('Unable to update financial line item')
			}
		}
		return rejectWithValue('Malformed financial line item (templateId and _id required)')
	},
)

export const deleteTemplateFinancialLineItem = createAsyncThunk(
	'templates/deleteFinancialLineItem',
	async (payload: any, { getState, rejectWithValue }) => {
		if (payload && payload._id && payload.templateId) {
			try {
				const { data } = await axios.delete(
					`/api/templates/${payload.templateId}/financialLineItem/${payload._id}`,
					prepConfig(getState),
				)
				return { _id: payload._id }
			} catch (error) {
				return rejectWithValue('Failed to delete financial line item')
			}
		}
		return rejectWithValue('Malformed financial line item (templateId and _id required)')
	},
)

export const createTemplateRisk = createAsyncThunk(
	'templates/createRisk',
	async (risk: any, { getState, rejectWithValue }) => {
		try {
			const { templateId, ...body } = risk
			const { data } = await axios.post(
				`/api/templates/${templateId}/risk`,
				body,
				prepConfig(getState),
			)
			return data
		} catch (error) {
			return rejectWithValue('Failed to create template risk')
		}
	},
)

export const initializeTemplateRisks = createAsyncThunk(
	'templates/initializeRisks',
	async (templateId: any, { getState, rejectWithValue }) => {
		try {
			return axios
				.get(`/api/templates/${templateId}/risk`, prepConfig(getState))
				.then(res => res.data)
		} catch (error) {
			return rejectWithValue('Failed to initialize template risks')
		}
	},
)

export const updateTemplateRisk = createAsyncThunk(
	'templates/updateRisk',
	async (payload: any, { getState, rejectWithValue }) => {
		if (payload && payload._id && payload.templateId) {
			try {
				const { templateId, _id, ...body } = payload
				const { data } = await axios.put(
					`/api/templates/${templateId}/risk/${_id}`,
					body,
					prepConfig(getState),
				)
				return data
			} catch (error) {
				return rejectWithValue('Unable to update template risk')
			}
		}
		return rejectWithValue('Malformed template risk (templateId and _id required)')
	},
)

export const deleteTemplateRisk = createAsyncThunk(
	'templates/deleteRisk',
	async (payload: any, { getState, rejectWithValue }) => {
		if (payload && payload._id && payload.templateId) {
			try {
				const { data } = await axios.delete(
					`/api/templates/${payload.templateId}/risk/${payload._id}`,
					prepConfig(getState),
				)
				return { _id: payload._id }
			} catch (error) {
				return rejectWithValue('Failed to delete template risk')
			}
		}
		return rejectWithValue('Malformed template risk (templateId and _id required)')
	},
)

export const createTemplateProCon = createAsyncThunk(
	'templates/createProCon',
	async (proCon: any, { getState, rejectWithValue }) => {
		try {
			const { templateId, ...body } = proCon
			const { data } = await axios.post(
				`/api/templates/${templateId}/proCon`,
				body,
				prepConfig(getState),
			)
			return data
		} catch (error) {
			return rejectWithValue('Failed to create template pro/con')
		}
	},
)

export const initializeTemplateProsCons = createAsyncThunk(
	'templates/initializeProsCons',
	async (templateId: any, { getState, rejectWithValue }) => {
		try {
			return axios
				.get(`/api/templates/${templateId}/proCon`, prepConfig(getState))
				.then(res => res.data)
		} catch (error) {
			return rejectWithValue('Failed to initialize template pros/cons')
		}
	},
)

export const updateTemplateProCon = createAsyncThunk(
	'templates/updateProCon',
	async (payload: any, { getState, rejectWithValue }) => {
		if (payload && payload._id && payload.templateId) {
			try {
				const { templateId, _id, ...body } = payload
				const { data } = await axios.put(
					`/api/templates/${templateId}/proCon/${_id}`,
					body,
					prepConfig(getState),
				)
				return data
			} catch (error) {
				return rejectWithValue('Unable to update template pro/con')
			}
		}
		return rejectWithValue('Malformed template pro/con (templateId and _id required)')
	},
)

export const deleteTemplateProCon = createAsyncThunk(
	'templates/deleteProCon',
	async (payload: any, { getState, rejectWithValue }) => {
		if (payload && payload._id && payload.templateId) {
			try {
				const { data } = await axios.delete(
					`/api/templates/${payload.templateId}/proCon/${payload._id}`,
					prepConfig(getState),
				)
				return { _id: payload._id }
			} catch (error) {
				return rejectWithValue('Failed to delete template pro/con')
			}
		}
		return rejectWithValue('Malformed template pro/con (templateId and _id required)')
	},
)

export const buildTemplateFromSiteDetails = createAsyncThunk(
	'templates/buildTemplateFromSiteDetails',
	async (templateData: any, { getState, rejectWithValue }) => {
		try {
			const { templateName, description, accessLevel, selectedDetails } = templateData
			const { data } = await axios.post(
				`/api/templates/build-site-details`,
				{
					templateName,
					description,
					accessLevel,
					selectedDetails,
				},
				prepConfig(getState),
			)
			return data
		} catch (error: any) {
			return rejectWithValue(error.response?.data?.message || 'Failed to build template')
		}
	},
)

// Extract the key out of the entitiy object
// eslint-disable-next-line no-unused-vars
const removeKey = (key, { [key]: _, ...rest }) => rest

const templatesSlice = createSlice({
	name: 'templates',
	initialState,
	reducers: {},
	extraReducers: {
		[createTemplate.fulfilled as any]: (state, action) => {
			const template = action.payload
			state.entities[template._id] = template
			state.isInitialized = false
		},
		[createTemplate.rejected as any]: (state, action) => {
			const template = action.payload
			state.entities[template._id] = template
		},

		[createTemplateSiteDetail.fulfilled as any]: (state, action) => {
			const sitedetail = action.payload
			state.siteDetails.entities[sitedetail._id] = sitedetail
		},
		[createTemplateSiteDetail.rejected as any]: (state, action) => {
			const sitedetail = action.payload
			state.siteDetails.entities[sitedetail._id] = sitedetail
		},

		[createTemplateTask.fulfilled as any]: (state, action) => {
			const task = action.payload
			state.tasks.entities[task._id] = task
		},
		[createTemplateTask.rejected as any]: (state, action) => {
			const task = action.payload
			state.tasks.entities[task._id] = task
		},

		[initializeTemplates.fulfilled as any]: (state, action) => {
			const templates = action.payload

			state.ids = templates?.map((item, index) => {
				state.entities[item._id] = item
				return item._id
			})
			state.isLoading = false
			state.isInitialized = true
		},
		[initializeTemplates.pending as any]: state => {
			state.isLoading = true
		},
		[initializeTemplates.rejected as any]: (state, action) => {
			state.isLoading = false
			state.isInitialized = true
			state.error = action.error.message
		},

		[initializeAdminTemplates.fulfilled as any]: (state, action) => {
			const templates = action.payload

			state.ids = templates?.map((item, index) => {
				state.entities[item._id] = item
				return item._id
			})
			state.isLoading = false
			state.isInitialized = true
		},
		[initializeAdminTemplates.pending as any]: state => {
			state.isLoading = true
		},
		[initializeAdminTemplates.rejected as any]: (state, action) => {
			state.isLoading = false
			state.isInitialized = true
			state.error = action.error.message
		},

		[initializeTemplateTasks.fulfilled as any]: (state, action) => {
			const tasks = action.payload

			state.tasks.ids = tasks?.map((item, index) => {
				state.tasks.entities[item._id] = item
				return item._id
			})
			state.tasks.isLoading = false
			state.tasks.isInitialized = true
		},
		[initializeTemplateTasks.pending as any]: state => {
			state.tasks.isLoading = true
		},
		[initializeTemplateTasks.rejected as any]: (state, action) => {
			state.tasks.isLoading = false
			state.tasks.isInitialized = true
			state.tasks.error = action.error.message
		},

		[initializeTemplateSiteDetails.fulfilled as any]: (state, action) => {
			const siteDetails = action.payload

			state.siteDetails.ids = siteDetails?.map((item, index) => {
				state.siteDetails.entities[item._id] = item
				return item._id
			})
			state.siteDetails.isLoading = false
			state.siteDetails.isInitialized = true
		},
		[initializeTemplateSiteDetails.pending as any]: state => {
			state.siteDetails.isLoading = true
		},
		[initializeTemplateSiteDetails.rejected as any]: (state, action) => {
			state.siteDetails.isLoading = false
			state.siteDetails.isInitialized = true
			state.siteDetails.error = action.error.message
		},

		[updateTemplate.fulfilled as any]: (state, action) => {
			const template = action.payload
			state.entities[template._id] = { ...action.payload, ...state.entities[template._id] }
		},

		[updateTemplateTask.fulfilled as any]: (state, action) => {
			const task = action.payload
			state.tasks.entities[task._id] = {
				...action.payload,
				...state.tasks.entities[task._id],
			}
		},

		[updateTemplateSiteDetail.fulfilled as any]: (state, action) => {
			const sitedetail = action.payload
			state.siteDetails.entities[sitedetail._id] = {
				...action.payload,
				...state.siteDetails.entities[sitedetail._id],
			}
		},

		[deleteTemplate.fulfilled as any]: (state, action) => {
			const id = action.meta.arg
			state.entities = removeKey(id, state.entities)
			state.ids = state.ids.filter(stateId => stateId !== id)
		},

		[deleteTemplateTask.fulfilled as any]: (state, action) => {
			const id = action.meta.arg
			state.tasks.entities = removeKey(id, state.tasks.entities)
			state.tasks.ids = state.tasks.ids.filter(stateId => stateId !== id)
		},

		[deleteTemplateSiteDetail.fulfilled as any]: (state, action) => {
			const id = action.meta.arg
			state.siteDetails.entities = removeKey(id, state.siteDetails.entities)
			state.siteDetails.ids = state.siteDetails.ids.filter(stateId => stateId !== id)
		},

		[buildTemplateFromTasks.fulfilled as any]: (state, action) => {
			const template = action.payload
			state.entities[template._id] = template
			if (!state.ids.includes(template._id)) {
				state.ids.push(template._id)
			}
			state.isInitialized = false
		},
		[buildTemplateFromTasks.rejected as any]: (state, action) => {
			// Handle error if needed
		},

		[buildTemplateFromSiteDetails.fulfilled as any]: (state, action) => {
			const template = action.payload
			state.entities[template._id] = template
			if (!state.ids.includes(template._id)) {
				state.ids.push(template._id)
			}
			state.isInitialized = false
		},
		[buildTemplateFromSiteDetails.rejected as any]: (state, action) => {
			// Handle error if needed
		},

		[createTemplateScoringCriterion.fulfilled as any]: (state, action) => {
			const criterion = action.payload
			state.scoringCriteria.entities[criterion._id] = criterion
		},

		[initializeTemplateScoringCriteria.fulfilled as any]: (state, action) => {
			const criteria = action.payload
			state.scoringCriteria.entities = {}
			state.scoringCriteria.ids = (criteria || []).map((item: any) => {
				state.scoringCriteria.entities[item._id] = item
				return item._id
			})
			state.scoringCriteria.isLoading = false
			state.scoringCriteria.isInitialized = true
		},
		[initializeTemplateScoringCriteria.pending as any]: state => {
			state.scoringCriteria.isLoading = true
		},
		[initializeTemplateScoringCriteria.rejected as any]: (state, action) => {
			state.scoringCriteria.isLoading = false
			state.scoringCriteria.isInitialized = true
			state.scoringCriteria.error = action.error.message
		},

		[updateTemplateScoringCriterion.fulfilled as any]: (state, action) => {
			const criterion = action.payload
			state.scoringCriteria.entities[criterion._id] = {
				...action.payload,
				...state.scoringCriteria.entities[criterion._id],
			}
		},

		[deleteTemplateScoringCriterion.fulfilled as any]: (state, action) => {
			const id = action.payload?._id
			if (!id) return
			state.scoringCriteria.entities = removeKey(id, state.scoringCriteria.entities)
			state.scoringCriteria.ids = state.scoringCriteria.ids.filter(
				stateId => stateId !== id,
			)
		},

		[createTemplateFinancialLineItem.fulfilled as any]: (state, action) => {
			const item = action.payload
			state.financialLineItems.entities[item._id] = item
		},

		[initializeTemplateFinancialLineItems.fulfilled as any]: (state, action) => {
			const items = action.payload
			state.financialLineItems.entities = {}
			state.financialLineItems.ids = (items || []).map((item: any) => {
				state.financialLineItems.entities[item._id] = item
				return item._id
			})
			state.financialLineItems.isLoading = false
			state.financialLineItems.isInitialized = true
		},
		[initializeTemplateFinancialLineItems.pending as any]: state => {
			state.financialLineItems.isLoading = true
		},
		[initializeTemplateFinancialLineItems.rejected as any]: (state, action) => {
			state.financialLineItems.isLoading = false
			state.financialLineItems.isInitialized = true
			state.financialLineItems.error = action.error.message
		},

		[updateTemplateFinancialLineItem.fulfilled as any]: (state, action) => {
			const item = action.payload
			state.financialLineItems.entities[item._id] = {
				...action.payload,
				...state.financialLineItems.entities[item._id],
			}
		},

		[deleteTemplateFinancialLineItem.fulfilled as any]: (state, action) => {
			const id = action.payload?._id
			if (!id) return
			state.financialLineItems.entities = removeKey(id, state.financialLineItems.entities)
			state.financialLineItems.ids = state.financialLineItems.ids.filter(
				stateId => stateId !== id,
			)
		},

		[createTemplateRisk.fulfilled as any]: (state, action) => {
			const risk = action.payload
			state.risks.entities[risk._id] = risk
		},

		[initializeTemplateRisks.fulfilled as any]: (state, action) => {
			const risks = action.payload
			state.risks.entities = {}
			state.risks.ids = (risks || []).map((item: any) => {
				state.risks.entities[item._id] = item
				return item._id
			})
			state.risks.isLoading = false
			state.risks.isInitialized = true
		},
		[initializeTemplateRisks.pending as any]: state => {
			state.risks.isLoading = true
		},
		[initializeTemplateRisks.rejected as any]: (state, action) => {
			state.risks.isLoading = false
			state.risks.isInitialized = true
			state.risks.error = action.error.message
		},

		[updateTemplateRisk.fulfilled as any]: (state, action) => {
			const risk = action.payload
			state.risks.entities[risk._id] = {
				...action.payload,
				...state.risks.entities[risk._id],
			}
		},

		[deleteTemplateRisk.fulfilled as any]: (state, action) => {
			const id = action.payload?._id
			if (!id) return
			state.risks.entities = removeKey(id, state.risks.entities)
			state.risks.ids = state.risks.ids.filter(stateId => stateId !== id)
		},

		[createTemplateProCon.fulfilled as any]: (state, action) => {
			const item = action.payload
			state.prosCons.entities[item._id] = item
		},

		[initializeTemplateProsCons.fulfilled as any]: (state, action) => {
			const items = action.payload
			state.prosCons.entities = {}
			state.prosCons.ids = (items || []).map((item: any) => {
				state.prosCons.entities[item._id] = item
				return item._id
			})
			state.prosCons.isLoading = false
			state.prosCons.isInitialized = true
		},
		[initializeTemplateProsCons.pending as any]: state => {
			state.prosCons.isLoading = true
		},
		[initializeTemplateProsCons.rejected as any]: (state, action) => {
			state.prosCons.isLoading = false
			state.prosCons.isInitialized = true
			state.prosCons.error = action.error.message
		},

		[updateTemplateProCon.fulfilled as any]: (state, action) => {
			const item = action.payload
			state.prosCons.entities[item._id] = {
				...action.payload,
				...state.prosCons.entities[item._id],
			}
		},

		[deleteTemplateProCon.fulfilled as any]: (state, action) => {
			const id = action.payload?._id
			if (!id) return
			state.prosCons.entities = removeKey(id, state.prosCons.entities)
			state.prosCons.ids = state.prosCons.ids.filter(stateId => stateId !== id)
		},
	} as any,
})

export default templatesSlice
